import { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav.jsx';
import PublicFooter from '../components/PublicFooter.jsx';
import PageMeta from '../components/PageMeta.jsx';
import { useTranslation } from '../i18n/I18nContext.jsx';
import { useLocalizedPath } from '../hooks/useLocalizedPath.js';

export default function ContactPage() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const subjects = t('contact.form.subjects');

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email.trim())) {
      setStatus('error');
      setErrorMsg(t('contact.form.errorInvalidEmail'));
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || '';
      const res = await fetch(`${apiBase}/contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || t('contact.form.errorGeneric'));
      }
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || t('contact.form.errorGeneric'));
    }
  }

  return (
    <>
      <PageMeta
        title={t('contact.meta.title')}
        description={t('contact.meta.description')}
        path="/contact"
      />

      <div className="pub-page">
        <PublicNav />

        <main id="main-content">
        <section className="page-hero">
          <div className="section-inner" style={{ textAlign: 'center' }}>
            <div className="section-label">{t('contact.header.label')}</div>
            <h1 className="page-hero-title">{t('contact.header.title')}</h1>
            <p className="page-hero-sub">{t('contact.header.sub')}</p>
          </div>
        </section>

        <section className="section">
          <div className="section-inner contact-layout">

            {/* Contact form */}
            <div className="contact-form-wrap">
              {status === 'success' ? (
                <div className="contact-success" role="status">
                  <div className="contact-success-icon" aria-hidden="true">{t('contact.success.icon')}</div>
                  <h2>{t('contact.success.title')}</h2>
                  <p>
                    {t('contact.success.body', { email: form.email || 'your address' })}
                  </p>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setStatus('idle')}
                  >
                    {t('contact.success.sendAnother')}
                  </button>
                </div>
              ) : (
                <form className="contact-form card" onSubmit={handleSubmit} noValidate
                  aria-label={t('contact.form.ariaLabel')}>
                  <h2 className="contact-form-title">{t('contact.form.title')}</h2>

                  {status === 'error' && (
                    <div className="alert alert-danger" role="alert">{errorMsg}</div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="name">
                        {t('contact.form.nameLabel')} <span aria-hidden="true">*</span>
                        <span className="sr-only">{t('contact.form.required')}</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        className="form-input"
                        placeholder={t('contact.form.namePlaceholder')}
                        value={form.name}
                        onChange={handleChange}
                        required
                        autoComplete="name"
                        aria-required="true"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">
                        {t('contact.form.emailLabel')} <span aria-hidden="true">*</span>
                        <span className="sr-only">{t('contact.form.required')}</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="text"
                        inputMode="email"
                        className="form-input"
                        placeholder={t('contact.form.emailPlaceholder')}
                        value={form.email}
                        onChange={handleChange}
                        autoComplete="email"
                        aria-required="true"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="subject">{t('contact.form.subjectLabel')}</label>
                    <select
                      id="subject"
                      name="subject"
                      className="form-input"
                      value={form.subject}
                      onChange={handleChange}
                    >
                      <option value="">{t('contact.form.subjectPlaceholder')}</option>
                      {Array.isArray(subjects) && subjects.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="message">
                      {t('contact.form.messageLabel')} <span aria-hidden="true">*</span>
                      <span className="sr-only">{t('contact.form.required')}</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      className="form-input form-textarea"
                      placeholder={t('contact.form.messagePlaceholder')}
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      aria-required="true"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={status === 'sending' || !form.name.trim() || !form.email.trim() || !form.message.trim()}
                    aria-busy={status === 'sending'}
                  >
                    {status === 'sending' ? t('contact.form.sending') : t('contact.form.send')}
                  </button>
                </form>
              )}
            </div>

            {/* Side info */}
            <aside className="contact-info" aria-label={t('contact.info.ariaLabel')}>
              <div className="contact-info-card card">
                <h3>{t('contact.info.title')}</h3>
                <div className="contact-info-row">
                  <span className="contact-info-icon" aria-hidden="true">📧</span>
                  <div>
                    <div className="contact-info-label">{t('contact.info.emailLabel')}</div>
                    <a href={`mailto:${t('contact.info.emailAddress')}`}>{t('contact.info.emailAddress')}</a>
                  </div>
                </div>
                <div className="contact-info-row">
                  <span className="contact-info-icon" aria-hidden="true">🔒</span>
                  <div>
                    <div className="contact-info-label">{t('contact.info.privacyLabel')}</div>
                    <a href={`mailto:${t('contact.info.privacyAddress')}`}>{t('contact.info.privacyAddress')}</a>
                  </div>
                </div>
                <div className="contact-info-row">
                  <span className="contact-info-icon" aria-hidden="true">⏱️</span>
                  <div>
                    <div className="contact-info-label">{t('contact.info.responseLabel')}</div>
                    <div>{t('contact.info.responseTime')}</div>
                  </div>
                </div>
              </div>

              <div className="contact-info-card card">
                <h3>{t('contact.info.quickTitle')}</h3>
                <p style={{ color: 'var(--color-muted)', fontSize: '0.9rem', margin: '0 0 0.75rem' }}>
                  {t('contact.info.quickBody')}
                </p>
                <Link to={lp('/dashboard')} className="btn btn-secondary" style={{ display: 'inline-block' }}>
                  {t('contact.info.quickCta')}
                </Link>
              </div>
            </aside>
          </div>
        </section>

        </main>
        <PublicFooter />
      </div>
    </>
  );
}
